import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { Trans, useTranslation } from 'react-i18next';
import { readAxiosDetail } from '../api_calls';
import { trackAnalyticsEvent } from '../../../services/analytics';
import { cleanupEvents, fetchSummary } from './analyticsApi';

import { Icon } from '@liftango/ui-icons';
interface DryRun {
  retentionDays: number;
  totalEvents: number;
  distinctNames: number;
}

const CONFIRM_TOKEN = 'DELETE';

export default function CleanupSection() {
  const { t } = useTranslation(['admin', 'common']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [retentionDays, setRetentionDays] = useState(90);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [running, setRunning] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<DryRun | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runDryRun = useCallback(async () => {
    setPreviewing(true);
    setError(null);
    setPreview(null);
    try {
      const cutoff = new Date(Date.now() - retentionDays * 24 * 60 * 60 * 1000);
      const until = cutoff.toISOString().slice(0, 10);
      const summary = await fetchSummary({ until });
      const totalEvents = summary.rows.reduce((s, r) => s + r.count, 0);
      const distinctNames = new Set(summary.rows.map((r) => r.event_name)).size;
      setPreview({ retentionDays, totalEvents, distinctNames });
      trackAnalyticsEvent('admin_analytics_cleanup_previewed', {
        retention_days: retentionDays,
        estimated_events: totalEvents,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setPreviewing(false);
    }
  }, [retentionDays]);

  const openConfirm = () => {
    setConfirmText('');
    setConfirmOpen(true);
  };

  const handleRun = async () => {
    setConfirmOpen(false);
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const res = await cleanupEvents(retentionDays);
      setResult(t('analytics.cleanup.complete', { count: res.deleted }));
      setPreview(null);
      trackAnalyticsEvent('admin_analytics_cleanup_executed', {
        retention_days: retentionDays,
        events_deleted: res.deleted,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setRunning(false);
    }
  };

  const tokenOk = confirmText.trim() === CONFIRM_TOKEN;

  return (
    <Box sx={{ maxWidth: { xs: '100%', md: 720 } }}>
      <Card variant="outlined">
        <CardHeader
          title={
            <Stack direction="row" spacing={1} alignItems="center">
              <Icon name="triangle-exclamation" size={20} />
              <Typography variant="subtitle1" fontWeight={700}>{t('analytics.cleanup.title')}</Typography>
            </Stack>
          }
          subheader={t('analytics.cleanup.subheader')}
          subheaderTypographyProps={{ variant: 'caption' }}
        />
        <CardContent>
          <Stack spacing={2}>
            {result && (
              <Alert severity="success" onClose={() => setResult(null)}>
                {result}
              </Alert>
            )}
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                label={t('analytics.cleanup.retentionDays')}
                type="number"
                size="small"
                value={retentionDays}
                onChange={(e) => {
                  setRetentionDays(Math.max(1, Number(e.target.value)));
                  setPreview(null);
                }}
                slotProps={{ htmlInput: { min: 1 } }}
                sx={{ maxWidth: { xs: '100%', sm: 200 } }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => void runDryRun()}
                disabled={previewing || running}
              >
                {previewing ? t('analytics.cleanup.calculating') : t('analytics.cleanup.previewImpact')}
              </Button>
            </Stack>

            {previewing && <Skeleton variant="rectangular" height={64} />}

            {preview && !previewing && (
              <Alert severity="warning" icon={<Icon size={20} name="triangle-exclamation" />}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    {t('analytics.cleanup.previewHeading', { days: preview.retentionDays })}
                  </Typography>
                  <Typography variant="body2">
                    <Trans
                      i18nKey="admin:analytics.cleanup.previewBody"
                      values={{
                        events: preview.totalEvents.toLocaleString(),
                        names: preview.distinctNames,
                        noun: t('analytics.cleanup.previewType', { count: preview.distinctNames }),
                      }}
                      components={[<strong key="0" />, <strong key="1" />]}
                    />
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('analytics.cleanup.previewCaveat')}
                  </Typography>
                </Stack>
              </Alert>
            )}

            <Box>
              <Button
                variant="contained"
                color="warning"
                onClick={openConfirm}
                disabled={running || !preview}
              >
                {running ? t('analytics.cleanup.running') : t('analytics.cleanup.runCleanup')}
              </Button>
              {!preview && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  {t('analytics.cleanup.runPreviewFirst')}
                </Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>{t('analytics.cleanup.confirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <Trans
              i18nKey="admin:analytics.cleanup.confirmBody"
              values={{ days: retentionDays }}
              components={[<strong key="0" />]}
            />
            {preview && (
              <Trans
                i18nKey="admin:analytics.cleanup.confirmBodyWithEstimate"
                values={{ events: preview.totalEvents.toLocaleString() }}
                components={[<strong key="0" />]}
              />
            )}
          </DialogContentText>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <Trans
              i18nKey="admin:analytics.cleanup.confirmType"
              values={{ token: CONFIRM_TOKEN }}
              components={[<strong key="0" />]}
            />
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={CONFIRM_TOKEN}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t('common:cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRun}
            disabled={!tokenOk}
          >
            {t('analytics.cleanup.deleteOld')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
